import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { supabase } from '../../../lib/supabase';
import { Button } from '../../common/ui/Button';
import BaseCard from '../../common/layout/BaseCard';
import { captureAppException } from '../../../utils/errorCaptureUtils';
import { captureCustomEvent } from '../../../utils/posthogUtils';
import { Dropdown } from '../../common/ui/Listbox';
import { ImageUpload } from '../../common/ui/ImageUpload';

interface ManageCommunityGeneralSettingsProps {
  communityId: string;
}

// Reusable component for access/visibility dropdowns with explanation
interface AccessVisibilityOption<T extends string> {
  id: T;
  text: string;
  explanation?: React.ReactNode;
  highlightClass?: string;
  disabled?: boolean;
}

interface AccessVisibilityDropdownProps<T extends string> {
  label: string;
  value: T;
  options: AccessVisibilityOption<T>[];
  onChange: (id: T) => void;
  className?: string;
}

function AccessVisibilityDropdown<T extends string>({ label, value, options, onChange, className = '' }: AccessVisibilityDropdownProps<T>) {
  const selected = options.find(opt => opt.id === value);
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      <Dropdown
        options={options.map(({ id, text, disabled }) => ({ id, text, disabled }))}
        value={value}
        onChange={opt => onChange(opt.id as T)}
        className="w-full"
        size="medium"
      />
      {selected?.explanation && (
        <div className={`mt-2 text-sm rounded-md px-3 py-2 ${selected.highlightClass || ''}`}>{selected.explanation}</div>
      )}
    </div>
  );
}

export const ManageCommunityGeneralSettings: React.FC<ManageCommunityGeneralSettingsProps> = ({ communityId }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [headerImage, setHeaderImage] = useState<string>('');
  const [avatarImage, setAvatarImage] = useState<string>('');
  const [accessControl, setAccessControl] = useState<'everyone' | 'ask_to_join' | 'invite_only'>('everyone');
  const [contentVisibility, setContentVisibility] = useState<'full' | 'restricted'>('full');
  const [discoverability, setDiscoverability] = useState<'public' | 'private' | 'hidden'>('public');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [uploadNotice, setUploadNotice] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Constants
  const MAX_SIZE_MB = 15;
  const BUCKET_NAME = 'community_images';
  const HEADER_IMAGE_PATH = `${communityId}/header.png`;

  const clearNotice = () => setNotice(null);

  // Get public URL for header image
  const getHeaderImageUrl = (path: string | null): string => {
    if (!path) return '';
    return supabase.storage.from(BUCKET_NAME).getPublicUrl(path).data.publicUrl;
  };

  // Get public URL for avatar image
  const getAvatarImageUrl = (path: string | null): string => {
    if (!path) return '';
    return supabase.storage.from(BUCKET_NAME).getPublicUrl(path).data.publicUrl;
  };

  // Header image handlers
  const handleHeaderImageChange = async (path: string | null) => {
    setHeaderImage(path || '');
    setUploadNotice({ type: 'success', message: path ? 'Header image updated successfully.' : 'Header image removed.' });
  };

  // Avatar image handlers
  const handleAvatarImageChange = async (path: string | null) => {
    setAvatarImage(path || '');
    setUploadNotice({ type: 'success', message: path ? 'Logo updated successfully.' : 'Logo removed.' });
  };

  useEffect(() => {
    const fetchCommunity = async () => {
      setInitialLoading(true);
      try {
        const { data, error } = await supabase
          .from('communities')
          .select('name, description, header_image, avatar_image, access_control, content_visibility, discoverability')
          .eq('id', communityId)
          .single();
        if (error) throw error;
        setName(data.name || '');
        setDescription(data.description || '');
        setHeaderImage(data.header_image || '');
        setAvatarImage(data.avatar_image || '');
        setAccessControl(data.access_control || 'everyone');
        setContentVisibility(data.content_visibility || 'full');
        setDiscoverability(data.discoverability || 'public');
        setNotice(null);
      } catch (error) {
        captureAppException(error, {
          tags: {
            context: 'manage_community_general_settings',
            action: 'fetch_community',
            community_id: communityId,
          }
        });
        toast.error('Failed to load community settings');
      } finally {
        setInitialLoading(false);
      }
    };
    fetchCommunity();
  }, [communityId]);

  const accessControlOptions: AccessVisibilityOption<typeof accessControl>[] = [
    {
      id: 'everyone',
      text: 'Everybody',
      explanation: 'All users can join this community directly without approval.',
      highlightClass: 'bg-blue-50 text-blue-700',
    },
    {
      id: 'ask_to_join',
      text: 'Ask to Join (Coming Soon)',
      explanation: 'Users can request to join, and an admin must approve',
      highlightClass: 'bg-green-50 text-green-700',
      disabled: true,
    },
    {
      id: 'invite_only',
      text: 'Invite-Only (Coming Soon)',
      explanation: 'Users must be added manually by a community admin',
      highlightClass: 'bg-yellow-50 text-yellow-700',
      disabled: true,
    },
  ];

  const handleSave = async () => {
    setLoading(true);
    
    captureCustomEvent('community_settings_updated', {
      eventProperties: {
        community_id: communityId,
        access_control: accessControl,
        content_visibility: contentVisibility,
        discoverability: discoverability,
        has_header_image: !!headerImage,
        has_avatar_image: !!avatarImage
      }
    });
    
    try {
      const { error } = await supabase
        .from('communities')
        .update({
          name,
          description,
          access_control: accessControl,
          content_visibility: contentVisibility,
          discoverability: discoverability,
        })
        .eq('id', communityId);

      if (error) throw error;
      
      setNotice({ type: 'success', message: 'Community settings updated successfully.' });
    } catch (error) {
      captureAppException(error, {
        tags: {
          context: 'manage_community_general_settings',
          action: 'save_settings',
          community_id: communityId,
        }
      });
      setNotice({ type: 'error', message: 'Failed to update community settings.' });
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <BaseCard>
        <div className="p-6 text-center text-gray-500">Loading community settings...</div>
      </BaseCard>
    );
  }

  return (
    <div className="space-y-8">
      {notice && (
        <div
          className={`p-4 rounded-md text-sm ${notice.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {notice.message}
          <button onClick={clearNotice} className="ml-2 text-sm font-semibold">Dismiss</button>
        </div>
      )}
      {uploadNotice && (
        <div
          className={`p-4 rounded-md text-sm mb-4 ${uploadNotice.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {uploadNotice.message}
          <button onClick={() => setUploadNotice(null)} className="ml-2 text-sm font-semibold">Dismiss</button>
        </div>
      )}

      <BaseCard>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Basic Information</h2>
        <p className="text-sm text-gray-500 mb-6">Update your community's basic details.</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              Community Name
            </label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => { setName(e.target.value); clearNotice(); }}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-hidden focus:ring-blue-500"
            />
          </div>

          <div className="md:col-span-2">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
              Description
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => { setDescription(e.target.value); clearNotice(); }}
              rows={4}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-hidden focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Images and Media Section */}
        <div className="border-t border-gray-200 pt-6 mt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Images and Media</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <ImageUpload 
                type="header"
                currentImageUrl={getHeaderImageUrl(headerImage)}
                onImageChange={handleHeaderImageChange}
                maxSizeMB={MAX_SIZE_MB}
                bucketName={BUCKET_NAME}
                filePath={HEADER_IMAGE_PATH}
                tableName="communities"
                columnName="header_image"
                recordId={communityId}
                label="Community Header Image"
                disabled={loading}
              />
              <p className="mt-2 text-sm text-gray-500">
                The header image appears at the top of your community page. Recommended size: 1200x400 pixels.
              </p>
            </div>

            <div>
              <ImageUpload
                type="logo"
                currentImageUrl={getAvatarImageUrl(avatarImage)}
                onImageChange={handleAvatarImageChange}
                maxSizeMB={MAX_SIZE_MB}
                bucketName={BUCKET_NAME}
                filePath={`${communityId}/logo.png`}
                tableName="communities"
                columnName="avatar_image"
                recordId={communityId}
                label="Community Logo"
                disabled={loading}
              />
              <p className="mt-8 text-sm text-gray-500">
                Your community logo appears in listings and next to your community name. Recommended size: 400x400 pixels.
              </p>
            </div>
          </div>
        </div>

        {/* Access and Visibility */}
        <div>
          <h3 className="text-lg font-semibold mb-2">Access and Visibility</h3>
          <p className="text-sm text-gray-600 mb-4 bg-yellow-50 border border-yellow-200 p-3 rounded-md">
            <strong>Note:</strong> Full access and visibility options are coming soon.
          </p>
          <div className="flex flex-col lg:flex-row lg:gap-6 space-y-4 lg:space-y-0">
            <AccessVisibilityDropdown
              label="Community Discoverability"
              value={discoverability}
              onChange={id => { setDiscoverability(id as typeof discoverability); clearNotice(); }}
              className="flex-1"
              options={[
                {
                  id: 'public',
                  text: 'Public',
                  explanation: 'All users will be able to find the community in the discovery section, and it can appear as recommended to them.',
                  highlightClass: 'bg-blue-50 text-blue-700',
                },
                {
                  id: 'hidden',
                  text: 'Unlisted',
                  explanation: 'The community will not appear in the discovery list, but anyone with the direct link will be able to view (even if not a member).',
                  highlightClass: 'bg-gray-100 text-gray-700',
                },
                {
                  id: 'private',
                  text: 'Private',
                  explanation: 'Only members of the community can see all details.',
                  highlightClass: 'bg-yellow-50 text-yellow-700',
                  disabled: true,
                },
              ]}
            />
            <AccessVisibilityDropdown
              label="Who can become a member?"
              value={accessControl}
              onChange={id => { setAccessControl(id as typeof accessControl); clearNotice(); }}
              className="flex-1"
              options={accessControlOptions}
            />
            <AccessVisibilityDropdown
              label="Who can see community content?"
              value={contentVisibility}
              onChange={id => { setContentVisibility(id as typeof contentVisibility); clearNotice(); }}
              className="flex-1"
              options={[
                {
                  id: 'full',
                  text: 'Public',
                  explanation: 'Content such as events, resources, and other members are visible to all. Community Updates are controlled individually.',
                  highlightClass: 'bg-blue-50 text-blue-700',
                },
                {
                  id: 'restricted',
                  text: 'Members Only (Coming Soon)',
                  explanation: 'Limit content such as events, resources, and other members to members only. Community Updates are controlled individually.',
                  highlightClass: 'bg-yellow-50 text-yellow-700',
                  disabled: true,
                },
              ]}
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-4 pt-6">
          <Button
            color="primary"
            variant="filled"
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </BaseCard>
    </div>
  );
}; 